import _uniqBy from 'lodash/uniqBy';
import formatDate from './date-time/utils';
import { DEFAULT_RECON_MESSAGES, STARTED } from '../../utils/case';
import { getReconCaseStatus } from '../../utils/case';
import { formatCost, formatDifference, getBenchmark } from '../../utils/cost';
import { INPATIENT_INDICATOR } from '../../utils/case';

export function prepareFormDataObject(object) {
  return Array.isArray(object) ? object.join('$|$') : object;
}

export function modifyServiceDetailQueryIfClosed(query, reconCase) {
  const caseID = reconCase ? reconCase.id || reconCase.caseID : null;
  if (query && !query.reconCaseID && (reconCase && reconCase.portalCaseStatus !== STARTED)) {
    query.reconCaseID = caseID;
  }
  return caseID;
}

export function formatEpisodeResponse(episodesData) {
  const formatted = episodesData?.map(episode => {
    const {
      patient = {},
      startDate = '',
      endDate = '',
      cost = '',
      costNP = '',
      peerGroup = '',
      weightedCostPercentile = '00.01',
      weightedCostPercentileNP = '00.01',
      pct25 = '',
      pct50 = '',
      pct75 = '',
      pct25NP = '',
      pct50NP = '',
      pct75NP = '',
      ...episodeRest
    } = episode;

    const {
      displayFirstName = '',
      displayLastName = '',
      displayMiddleInitial = '',
      birthDate = '',
      disenrollDate = '',
      ...patientRest
    } = patient;

    const start = formatDate(startDate);
    const end = formatDate(endDate);
    const timeFrame = start === end ? start : `${start} - ${end}`;

    return {
      ...episodeRest, // Only send episode data that's not been formatted
      startDate: start,
      endDate: end,
      timeFrame,
      actualCost: formatCost(cost),
      actualCostNP: formatCost(costNP),
      benchmarkCost: formatCost(peerGroup),
      costDifference: formatDifference(cost - peerGroup),
      percentile: parseFloat((weightedCostPercentile * 100).toFixed(2))
        ? (weightedCostPercentile * 100).toFixed(2)
        : '00.01',
      percentileNP: parseFloat((weightedCostPercentileNP * 100).toFixed(2))
        ? (weightedCostPercentileNP * 100).toFixed(2)
        : '00.01',
      benchmarkDisplay: getBenchmark(cost, peerGroup),
      ...patientRest, // Only send patient data that's not been formatted
      fullName: `${displayLastName}, ${displayFirstName} ${displayMiddleInitial}`,
      birthDate: formatDate(birthDate),
      disenrollDate: formatDate(disenrollDate),
      pct25: formatCost(pct25),
      pct50: formatCost(pct50),
      pct75: formatCost(pct75),
      pct25NP: formatCost(pct25NP),
      pct50NP: formatCost(pct50NP),
      pct75NP: formatCost(pct75NP)
    };
  });
  return formatted;
}

export function formatCostPatientResponse(costPatientData: Array<{ [key: string]: any }>) {
  const formatted = costPatientData?.map((episode: { [key: string]: any }) => {
    const {
      actualCost = '',
      actualCostNP = '',
      benchmarkCost = '',
      weightedCostPercentile = '00.01',
      weightedCostPercentileNP = '00.01',
      weightAdjustment = 0,
      patientBirthDate = '',
      displayFirstName = '',
      displayLastName = '',
      displayMiddleInitial = '',
      riskScore = 0,
      pct25 = '',
      pct50 = '',
      pct75 = '',
      pct25NP = '',
      pct50NP = '',
      pct75NP = '',
      ...episodeRest
    } = episode;

    return {
      ...episodeRest, // Only send data that's not been formatted
      actualCost: formatCost(actualCost),
      actualCostNP: (actualCostNP) ? formatCost(actualCostNP) : '$0.00',
      benchmarkCost: formatCost(benchmarkCost),
      costDifference: formatDifference(benchmarkCost - actualCost),
      percentile: parseFloat((weightedCostPercentile * 100).toFixed(2))
        ? (weightedCostPercentile * 100).toFixed(2)
        : '00.01',
      percentileNP: parseFloat((weightedCostPercentileNP * 100).toFixed(2))
        ? (weightedCostPercentileNP * 100).toFixed(2)
        : '00.01',
      benchmarkDisplay: getBenchmark(actualCost, benchmarkCost),
      fullName: `${displayLastName}, ${displayFirstName} ${displayMiddleInitial}`,
      birthDate: formatDate(patientBirthDate),
      riskScore: riskScore.toFixed(2),
      weightAdjustment: weightAdjustment,
      pct25: formatCost(pct25),
      pct50: formatCost(pct50),
      pct75: formatCost(pct75),
      pct25NP: formatCost(pct25NP),
      pct50NP: formatCost(pct50NP),
      pct75NP: formatCost(pct75NP)
    };
  });
  return formatted;
}

export function formatServiceDetailsReconGridData(returnData = {}, query = {}) {
  const { totalItems = 0, data = [] } = returnData;

  const formatted =
    data && data.length
      ? data.map(detail => {
        detail.serviceDate = formatDate(detail.serviceDate);
        detail.providerName = `${detail.providerFirstName} ${detail.providerLastName}`;
        delete detail['providerFirstName'];
        delete detail['providerLastName'];
        return detail;
      })
      : [];

  return {
    data: formatted,
    totalItems,
    currentPage: query.page ? Number(query.page) : 0,
    previousQuery: JSON.parse(JSON.stringify(query))
  };
}

export function getReconData(data: Array<{ [key: string]: any }>, fields: { [key: string]: any }) {
  const {
    selectedIDFieldName,
    selectedID,
    reasonDescriptionName,
    svcDetailsIDField,
    svcDetailsDescField,
    severityOrRiskDescriptionField,
    severityOrRiskStateVariableName
  } = fields;
  // service exclusion reason description
  const reasonDescription =
    Array.isArray(data) && data.length > 0 && reasonDescriptionName in data[0]
      ? data[0][reasonDescriptionName]
      : '';

  // service details exclusions
  let serviceDetails = _uniqBy(data, svcDetailsIDField).map(svc => ({
    serviceDetailId: svc[svcDetailsIDField],
    reasonDescription: svc[svcDetailsDescField]
  }));

  if (serviceDetails.length === 1 && serviceDetails[0].serviceDetailId === 0) {
    serviceDetails = null;
  }
  // severity or risk exclusions
  const otherText =
    Array.isArray(data) && data.length > 0 && severityOrRiskDescriptionField in data[0]
      ? data[0][severityOrRiskDescriptionField]
      : '';

  // the following are duplicated in the array so just grab from the 1st element
  const {
    reconCaseSystemID,
    caseOpenIndicator,
    changeRequestApplied,
    changeRequestDecision,
    requestID,
    conclusion,
    decision,
    investigation,
    submittedDate,
    attachmentName
  } = (Array.isArray(data) && data.length > 0 ? data[0] : {});

  const conclusionText = conclusion || 'N/A';
  const decisionText = decision || 'N/A';

  return {
    [selectedIDFieldName]: selectedID,
    attachmentName,
    reasonDescription,
    serviceDetailExclusions: serviceDetails,
    [severityOrRiskStateVariableName]: {
      otherText,
      reasonDescription: otherText.length ? 'Y' : ''
    },
    caseID: reconCaseSystemID,
    caseOpenIndicator,
    changeRequestApplied,
    changeRequestDecision,
    changeRequestID: requestID,
    conclusion: caseOpenIndicator === 'N' ? conclusionText : DEFAULT_RECON_MESSAGES.CONCLUSION,
    decision: caseOpenIndicator === 'N' ? decisionText : DEFAULT_RECON_MESSAGES.DECISION,
    investigation,
    submittedDate: formatDate(submittedDate),
    status: getReconCaseStatus(submittedDate, caseOpenIndicator)
  };
};


export const computeDiagnosisData = (item: { [key: string]: any }) => {
  const diagnosisData = [];
  for (let i = 0; i < 10; i++) {
    const end = i < 9 ? `0${i + 1}` : '10';
    diagnosisData.push({
      number: end,
      code: item[`diagnosisCode${end}`],
      description: item[`diagnosisDescription${end}`],
      flag: item[`diagnosisFlag${end}`],
    });
  }
  return diagnosisData;
};

export const formatServerityConditions = (data: Array<{ [key: string]: any }>) => {
  if (data.length) {
    // Data is duped for attributes of a certain type
    // Grab what's needed from first record
    const firstRecord = data[0];
    const { inpatientOrOutpatient = '' } = firstRecord;

    if (inpatientOrOutpatient === INPATIENT_INDICATOR) {
      const { description = '', code = '' } = firstRecord;
      return {
        code,
        description,
        inpatientOrOutpatient
      };
    }

    const description = (data as Array<{ description: string }>).map(d => d.description).join(', ');
    return {
      description,
      inpatientOrOutpatient
    };
  }
  return {
    description: '',
    inpatientOrOutpatient: ''
  }
};

export const formatRiskConditions = (data: Array<{ [key: string]: any }>) => {

  let actual = '';
  if (data) {
    actual = data
      .filter(condition => {
        return condition.actOrPotential !== 'POTENTIAL';
      })
      .map(condition => {
        return condition.description;
      })
      .join('; ');
  }
  return { actual };
}

